import React, { useEffect, useState } from "react";
import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { keyframes } from "@mui/system";
import { getServices } from "../services/api/getAll";
import ServicesListMobile from "./ServicesListMobile";
import { useTranslation } from "react-i18next";
import BookingForm from "./BookingForm";

// Import new premium service images
import serviceTowing from "../assets/service_towing.jpg";
import serviceTire from "../assets/service_tire.jpg";
import serviceBattery from "../assets/service_battery.jpg";
import serviceFuel from "../assets/service_fuel.jpg";
import serviceWash from "../assets/service_wash.jpg";

// Pulse animation for interactive hotspots
const pulse = keyframes`
  0% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(255, 174, 0, 0.8);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 12px rgba(255, 174, 0, 0);
  }
  100% {
    transform: scale(0.9);
    box-shadow: 0 0 0 0 rgba(255, 174, 0, 0);
  }
`;

const servicesData = [
  {
    id: "towing",
    image: serviceTowing,
    number: "01",
    arTitle: "خدمة الونش والإنقاذ السريع",
    arDesc: "خدمة ونش وإنقاذ متكاملة على مدار الساعة لتأمين رحلتك. في TowHelp، نقوم بتوصيلك بأقرب مقدمي خدمات الونش الموثوقين لنقل سيارتك بأمان وسرعة مع تجربة سهلة وموثوقة من لحظة طلب الخدمة حتى وصول المساعدة إليك.",
    enTitle: "Towing & Rapid Rescue",
    enDesc: "A comprehensive 24/7 towing and roadside rescue service to keep your journey safe. At TowHelp, we connect you with the nearest trusted tow service providers to transport your car safely and swiftly, with a seamless and reliable experience from the moment you request the service until help reaches you.",
    hotspots: [
      { top: "32%", left: "55%", titleEn: "Hydraulic Platform", descEn: "Flatbed lifts safely without scratching bumpers.", titleAr: "رافعة هيدروليكية", descAr: "منصة رفع مستوية لحماية السيارة بالكامل من الخدوش." },
      { top: "60%", left: "70%", titleEn: "GPS Tracking", descEn: "Real-time dispatch system routes the nearest truck.", titleAr: "تتبع فوري GPS", descAr: "توجيه أسرع شاحنة إنقاذ إليك بناءً على موقعك الجغرافي." }
    ]
  },
  {
    id: "tire",
    image: serviceTire,
    number: "02",
    arTitle: "إصلاح وتغيير الإطارات المتنقل",
    arDesc: "لا تدع ثقب الإطار يعطل يومك. نصل إليك أينما كنت بأجهزة فك وتركيب هوائية محترفة لتغيير الإطار أو إصلاحه فوراً على الطريق. نقوم بمعايرة ضغط الهواء وفحص السلامة لضمان عودتك للقيادة بأمان ودون عناء.",
    enTitle: "Mobile Tire Repair & Replacement",
    enDesc: "Don't let a flat tire ruin your day. We come to you wherever you are, equipped with professional pneumatic tools to replace or repair your tire instantly on the road. We calibrate tire pressure and perform a safety check to ensure you get back to driving safely and hassle-free.",
    hotspots: [
      { top: "65%", left: "45%", titleEn: "Pneumatic Tools", descEn: "Fast tyre release and mount using professional impact wrenches.", titleAr: "معدات هوائية", descAr: "فك وتركيب فوري وسريع للإطارات باستخدام مفاتيح هوائية محترفة." },
      { top: "50%", left: "30%", titleEn: "Pressure Calibration", descEn: "Precision digital testing for optimal road grip and safety.", titleAr: "ضبط ضغط الإطارات", descAr: "معايرة دقيقة للهواء لضمان ثبات السيارة وأمان القيادة." }
    ]
  },
  {
    id: "battery",
    image: serviceBattery,
    number: "03",
    arTitle: "خدمة وصلة البطارية (Jump Start)",
    arDesc: "إذا تعطلت بطارية سيارتك فجأة ولم تتمكن من تشغيلها، يصل إليك فريق TowHelp بسرعة لتقديم خدمة وصلة البطارية (Jump Start) باحترافية، لتتمكن من استكمال رحلتك بأسرع وقت ممكن.",
    enTitle: "Battery Jump Start Service",
    enDesc: "If your car battery suddenly dies and you can't start it, the TowHelp team reaches you quickly to provide a professional Jump Start service, so you can resume your journey as fast as possible.",
    hotspots: [
      { top: "45%", left: "50%", titleEn: "Smart Diagnostics", descEn: "We test your battery health before proposing jumpstart or replacement.", titleAr: "فحص ذكي للبطارية", descAr: "تحليل دقيق لكفاءة البطارية ودينامو السيارة قبل اتخاذ قرار الاستبدال." },
      { top: "60%", left: "68%", titleEn: "Certified Brands", descEn: "New batteries with official warranty up to 2 years.", titleAr: "بطاريات معتمدة", descAr: "استبدال فوري ببطاريات أصلية مكفولة بضمان رسمي يصل لعامين." }
    ]
  },
  {
    id: "fuel",
    image: serviceFuel,
    number: "04",
    arTitle: "توصيل الوقود في حالات الطوارئ",
    arDesc: "هل نفد منك الوقود أثناء الطريق؟ لا داعي للقلق! في TowHelp، بنوصلك بسرعة بكمية تصل إلى 4 لتر من الوقود لتتمكن من الوصول إلى أقرب محطة وقود واستكمال رحلتك بأمان ودون عناء.\n(تتوفر الخدمة وفقًا لنوع الوقود المناسب لسيارتك (92 أو 95))",
    enTitle: "Emergency Fuel Delivery",
    enDesc: "Ran out of fuel on the road? Don't worry! At TowHelp, we quickly deliver up to 4 liters of fuel so you can reach the nearest gas station and continue your journey safely and hassle-free.\n(Service is available with the fuel type suitable for your car — Octane 92 or 95)",
    hotspots: [
      { top: "68%", left: "15%", titleEn: "Clean Fuel", descEn: "Octane 92 or 95 filtered fuels delivered securely.", titleAr: "وقود نقي ومصفى", descAr: "توصيل بنزين 92 أو 95 خالٍ من الشوائب ومطابق للمواصفات." },
      { top: "45%", left: "28%", titleEn: "Safe Refueling", descEn: "Handled by experts to prevent gas spills and paint damage.", titleAr: "تعبئة آمنة", descAr: "عملية تعبئة نظيفة تمنع انسكاب الوقود أو إلحاق الضرر بطلاء السيارة." }
    ]
  },
  {
    id: "wash",
    image: serviceWash,
    number: "05",
    arTitle: "خدمات الغسيل والكار كير المتنقل",
    arDesc: "استمتع بخدمة غسيل وعناية متكاملة لسيارتك أمام منزلك أو مقر عملك، باستخدام معدات ومواد عالية الجودة تمنح سيارتك نظافة ولمعانًا احترافيًا دون الحاجة إلى إضاعة وقتك في الذهاب إلى مراكز الغسيل.",
    enTitle: "Mobile Washing & Car Care Services",
    enDesc: "Enjoy a complete washing and car care service right at your doorstep or office, using high-quality equipment and materials that give your car a professional clean and shine — without wasting your time going to car wash centers.",
    hotspots: [
      { top: "40%", left: "55%", titleEn: "High-Pressure Wash", descEn: "Removes deep grime without micro-scratching paint layers.", titleAr: "غسيل بضغط عالٍ", descAr: "تنظيف الأتربة والطين بدون أي خدوش دقيقة لطلاء السيارة." },
      { top: "65%", left: "85%", titleEn: "Premium Waxing", descEn: "Gloss protective layer shielding against dust and UV rays.", titleAr: "تلميع وحماية بالشمع", descAr: "طبقة شمع واقية تمنح سيارتك لمعاناً براقاً وتحميها من أشعة الشمس والأتربة." }
    ]
  }
];

const ServicesList = () => {
  const [services, setServices] = useState<any[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoveredHotspot, setHoveredHotspot] = useState<number | null>(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const currentLang = i18n.language.startsWith("ar") ? "ar" : "en";

  const mobile = useMediaQuery("(max-width: 1100px)");

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getServices();
        setServices(response?.items || []);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  // Set up scroll listener for scroll-triggered visuals on desktop
  useEffect(() => {
    if (mobile) return;

    const handleScroll = () => {
      const sections = document.querySelectorAll(".service-scroll-section");
      let currentActive = 0;
      let minDistance = Infinity;
      const centerY = window.innerHeight / 2;

      sections.forEach((sec, idx) => {
        const rect = sec.getBoundingClientRect();
        const elementCenter = rect.top + rect.height / 2;
        const distance = Math.abs(centerY - elementCenter);
        if (distance < minDistance) {
          minDistance = distance;
          currentActive = idx;
        }
      });

      setActiveIdx(currentActive);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobile]);

  if (mobile) return <ServicesListMobile />;

  // Map API services to local high-quality templates
  const displayServices = (services.length > 0 ? services : servicesData).map((s: any, idx: number) => {
    // Choose template by index matching
    const template = servicesData[idx % servicesData.length];

    // Ensure our high-quality promotional descriptions and images are used consistently
    const title = currentLang === "ar" ? template.arTitle : template.enTitle;
    const desc = currentLang === "ar" ? template.arDesc : template.enDesc;

    return {
      id: s.id || template.id,
      number: template.number,
      image: template.image,
      hotspots: template.hotspots,
      title,
      desc,
    };
  });
  return (
    <Box
      id="services"
      sx={{
        pt: 12,
        pb: 18,
        position: "relative",
        background: "linear-gradient(180deg, #060606 0%, #0d0d0d 100%)",
      }}
    >
      {/* Mesh Background */}
      <Box
        className="bg-mesh-overlay"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Floating Ambient Glowing Blobs */}
      <Box
        sx={{
          position: "absolute",
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.06) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(90px)",
          top: "10%",
          left: isRTL ? "auto" : "5%",
          right: isRTL ? "5%" : "auto",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: "700px",
          height: "700px",
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.04) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(100px)",
          bottom: "20%",
          right: isRTL ? "auto" : "5%",
          left: isRTL ? "5%" : "auto",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Title */}
      <Typography
        component={motion.h3}
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        variant="h3"
        fontWeight={900}
        textAlign="center"
        mb={8}
        sx={{
          fontFamily: "Poppins, Ozen",
          background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textTransform: "uppercase",
          zIndex: 2,
          position: "relative",
          textShadow: "0 0 35px rgba(255, 174, 0, 0.15)",
        }}
      >
        {t("whatWeProvide")}
      </Typography>

      {/* Main Split Section for Desktop Storytelling */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isRTL ? "row-reverse" : "row",
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          px: "5%",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Left Side: Sticky Visual Container */}
        <Box
          sx={{
            width: "45%",
            height: "80vh",
            position: "sticky",
            top: "10vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "flex-start",
          }}
        >
          {/* Framed Glowing Wrapper */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "520px",
              height: "380px",
              position: "relative",
              borderRadius: "24px",
              border: "2px solid rgba(255, 174, 0, 0.2)",
              background: "#080808",
              boxShadow: "0 25px 65px rgba(0, 0, 0, 0.65), 0 0 30px rgba(255, 174, 0, 0.03)",
              overflow: "visible",
            }}
          >
            {/* Rounded clipping container for the images */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                borderRadius: "22px",
                overflow: "hidden",
                zIndex: 1,
              }}
            >
              {/* Image Transition Screen */}
              <AnimatePresence mode="wait">
                {displayServices[activeIdx] && (
                  <Box
                    component={motion.img}
                    key={displayServices[activeIdx].id}
                    src={displayServices[activeIdx].image}
                    alt={displayServices[activeIdx].title}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </AnimatePresence>
            </Box>

            {/* Hotspots layer */}
            {displayServices[activeIdx] &&
              displayServices[activeIdx].hotspots.map((hs: any, hIdx: number) => {
                const isHovered = hoveredHotspot === hIdx;
                return (
                  <Box
                    key={hIdx}
                    sx={{
                      position: "absolute",
                      top: hs.top,
                      left: hs.left,
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    {/* Glowing Pulse Dot */}
                    <Box
                      onMouseEnter={() => setHoveredHotspot(hIdx)}
                      onMouseLeave={() => setHoveredHotspot(null)}
                      sx={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        background: "#FFAE00",
                        border: "2px solid white",
                        cursor: "pointer",
                        animation: `${pulse} 2s infinite ease-in-out`,
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.25)",
                        },
                      }}
                    />

                    {/* Popover Card */}
                    <AnimatePresence>
                      {isHovered && (
                        <Box
                          component={motion.div}
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          sx={{
                            position: "absolute",
                            bottom: "28px",
                            left: "-110px",
                            width: "220px",
                            p: 2,
                            borderRadius: "14px",
                            background: "rgba(10, 10, 10, 0.85)",
                            backdropFilter: "blur(12px)",
                            border: "1.5px solid rgba(255, 174, 0, 0.35)",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                            color: "white",
                            pointerEvents: "none",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={800}
                            sx={{ color: "#FFAE00", fontSize: "0.85rem", mb: 0.5 }}
                          >
                            {currentLang === "ar" ? hs.titleAr : hs.titleEn}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#dddddd", fontSize: "0.75rem", lineHeight: 1.4 }}>
                            {currentLang === "ar" ? hs.descAr : hs.descEn}
                          </Typography>
                        </Box>
                      )}
                    </AnimatePresence>
                  </Box>
                );
              })}
          </Box>
        </Box>

        {/* Right Side: Scroll Content blocks */}
        <Box sx={{ width: "55%", pl: isRTL ? 0 : 8, pr: isRTL ? 8 : 0 }}>
          {displayServices.map((service, idx) => {
            const isActive = activeIdx === idx;

            return (
              <Box
                key={service.id}
                className="service-scroll-section"
                dir={isRTL ? "rtl" : "ltr"}
                sx={{
                  minHeight: "65vh",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  py: 6,
                  position: "relative",
                  transition: "opacity 0.6s ease, transform 0.6s ease",
                  opacity: isActive ? 1 : 0.25,
                  transform: isActive ? "scale(1)" : "scale(0.97)",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {/* Large Background progress number */}
                <Typography
                  sx={{
                    position: "absolute",
                    top: "10%",
                    [isRTL ? "left" : "right"]: 0,
                    fontSize: "8.5rem",
                    fontWeight: 900,
                    lineHeight: 1,
                    fontFamily: "Poppins",
                    WebkitTextStroke: "1px rgba(255, 174, 0, 0.08)",
                    color: "transparent",
                    userSelect: "none",
                    zIndex: -1,
                  }}
                >
                  {service.number}
                </Typography>

                {/* Subtitle tag */}
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "3px",
                    color: "#FFAE00",
                    fontWeight: 700,
                    mb: 1.5,
                    display: "block",
                  }}
                >
                  {currentLang === "ar" ? `الخدمة ${service.number}` : `SERVICE ${service.number}`}
                </Typography>

                {/* Service Title */}
                <Typography
                  variant="h4"
                  fontWeight={900}
                  mb={2.5}
                  sx={{
                    fontFamily: "Poppins, Ozen",
                    color: isActive ? "#FFAE00" : "#ffffff",
                    transition: "color 0.4s ease",
                    textShadow: isActive ? "0 0 25px rgba(255, 174, 0, 0.15)" : "none",
                  }}
                >
                  {service.title}
                </Typography>

                {/* Service Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: "#cccccc",
                    fontFamily: "Poppins, Ozen",
                    lineHeight: 1.8,
                    fontSize: "1.05rem",
                    maxWidth: "500px",
                    mb: 4,
                  }}
                >
                  {service.desc}
                </Typography>

                {/* Interactive Booking CTA */}
                <Box>
                  <Button
                    component="a"
                    href="#booking-section"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById("booking-section")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    sx={{
                      borderRadius: "30px",
                      px: 4,
                      py: 1.3,
                      background: "linear-gradient(135deg, #FFAE00 0%, #FF8F00 100%)",
                      color: "#000",
                      fontWeight: 800,
                      fontFamily: "Poppins",
                      boxShadow: "0 6px 20px rgba(255, 174, 0, 0.25)",
                      transition: "all 0.3s ease",
                      textTransform: "none",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 10px 25px rgba(255, 174, 0, 0.45)",
                        background: "linear-gradient(135deg, #FFC107 0%, #FFAE00 100%)",
                      },
                    }}
                  >
                    {currentLang === "ar" ? "احجز الخدمة الآن" : "Book Service Now"}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Booking Form section */}
      <Box
        id="booking-section"
        component={motion.div}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
        sx={{ position: "relative", zIndex: 2, mt: 10 }}
      >
        <BookingForm />
      </Box>
    </Box>
  );
};

export default ServicesList;
